import type { TypeToModel } from "../type-to-model"

export type FindFn = <ModelName extends keyof TypeToModel>(
  modelName: ModelName,
  id: string,
) => TypeToModel[ModelName]

export type PRIMARY_POSSESSIVE = "owns" | "others" | "collaborative" | "unknown"
export type ADVERBIAL_POSSESSIVE = "owns" | "others" | "collaborative" | "unknown"

/**
 * The verb list should stay short so we don't have similiar verbs that are
 * essentially the same
 */
export type PRIMARY_VERB =
  | "analyze"
  | "click"
  | "comment"
  | "create"
  | "copy"
  | "customize"
  | "delete"
  | "download"
  | "edit"
  | "email"
  | "move"
  | "open"
  | "print"
  | "search"
  | "share"
  | "unlock"
  | "use"
  | "view"
  | null

/**
 * This should stay a smaller, more thoughtful list. The more objects, the more
 * things to think about. We want to keep these meaningful.
 */
export type PRIMARY_OBJECT =
  | "app"
  | "attachment"
  | "class_website"
  | "class_website_lesson"
  | "clipboard"
  | "course"
  | "google_classroom_post"
  | "group"
  | "help_article"
  | "lesson"
  | "marketing_site"
  | "planbook"
  | "session"
  | "standards_tracker"
  | "template"
  | "unit"
  | "user"

/**
 * We should expect this to get quite long as there are quite a few
 * dimensions to objects. It's not a problem if this grows, but we should make
 * sure there isn't duplication.
 */
export type PRIMARY_OBJECT_SPECIFIER =
  | "ai"
  | "attachments"
  | "automatic_templates"
  | "branding"
  | "calendar"
  | "cards"
  | "class_websites"
  | "collaborators"
  | "color"
  | "comments"
  | "contact_us_page"
  | "course_create"
  | "courses"
  | "cover"
  | "dashboard"
  | "day"
  | "editor"
  | "email_link_button"
  | "enter_times_state"
  | "feedback"
  | "filter_classes"
  | "followed_course"
  | "full_version"
  | "google_classroom"
  | "history"
  | "import"
  | "instructional_hub"
  | "integrations"
  | "is_non_instructional"
  | "lesson"
  | "lesson_slot"
  | "line_up_classes"
  | "month"
  | "multiple_standard_sets"
  | "onboarding"
  | "outline"
  | "people_index"
  | "people_show"
  | "planbook_onboarding"
  | "planbook_share_menu"
  | "redo"
  | "repeating_schedule"
  | "school"
  | "school_network"
  | "search_page"
  | "search_box"
  | "sharing"
  | "single_standard_set"
  | "slug"
  | "staff_index"
  | "staff_show"
  | "standards"
  | "standards_search"
  | "standards_tracker"
  | "student_version"
  | "template_assignment"
  | "templates"
  | "time"
  | "timeline"
  | "title"
  | "undo"
  | "unit"
  | "week"
  | null

/**
 * The verb list should stay short so we don't have similiar verbs that are
 * essentially the same
 */
export type ADVERBIAL_VERB =
  | "accepting"
  | "adding"
  | "bumping"
  | "clicking"
  | "copying"
  | "downloading"
  | "dragging_dropping"
  | "editing"
  | "emailing"
  | "importing"
  | "navigating"
  | "opening"
  | "pasting"
  | "renaming"
  | "removing"
  | "reordering"
  | "resizing"
  | "responding"
  | "saving"
  | "searching"
  | "selecting"
  | "sending"
  | "starring"
  | "toggling"
  | "uploading"
  | "viewing"
  | "visiting"
  | null

export type ADVERBIAL_OBJECT =
  | "annotation"
  | "article"
  | "attachment"
  | "card"
  | "chat"
  | "collaborator"
  | "color"
  | "comment"
  | "course_order"
  | "course"
  | "day"
  | "day_card"
  | "google_classroom"
  | "image"
  | "jurisdiction"
  | "learn_more"
  | "lesson"
  | "lesson_slot"
  | "line_up_classes"
  | "link"
  | "member"
  | "message"
  | "modal"
  | "planbook"
  | "public_access"
  | "reaction"
  | "rotation"
  | "rotation_day_off"
  | "screen"
  | "standard_set_pane"
  | "standard"
  | "subject"
  | "template"
  | "unit"
  | "week"
  | "version"
  | null

/**
 * This will grow unboundedly.
 */
export type ADVERBIAL_OBJECT_SPECIFIER =
  | "accept"
  | "admin"
  | "all"
  | "back_to_pane"
  | "backward"
  | "between_lessons"
  | "content"
  | "color"
  | "district-admin"
  | "dropbox_file"
  | "entire_day"
  | "entire_object"
  | "final_screen"
  | "forward"
  | "gc_max_points"
  | "gc_mc_questions"
  | "gc_schedule"
  | "gc_share_mode"
  | "gc_time"
  | "gc_topic"
  | "gc_unschedule"
  | "google_drive_file"
  | "group"
  | "group-membership-accepted"
  | "group-membership-invited"
  | "group-membership-potential"
  | "group-membership-requested"
  | "happy_face"
  | "id"
  | "lesson"
  | "message"
  | "name"
  | "no_school"
  | "off"
  | "off-and-delete"
  | "off-and-bump"
  | "on"
  | "one_drive_file"
  | "order"
  | "other"
  | "permissions"
  | "public"
  | "qr_code"
  | "range"
  | "rate_limited"
  | "reject"
  | "reset"
  | "revise"
  | "role"
  | "sad_face"
  | "select_jurisdiction"
  | "select_subject"
  | "single"
  | "single_lesson"
  | "skip"
  | "specific"
  | "sub_unit"
  | "suggestion"
  | "teacher"
  | "template_chooser"
  | "template_evaluation"
  | "title"
  | "to_different_class"
  | "unit"
  | "upload_file"
  | "user"
  | "value"
  | "week"
  | "within_lesson"
  | null

export interface AnalyticsBase {
  /** If set, this must be a dateString (ISO 8601 date). */
  date_plan_scheduled_for: string | null
  root_id: string | null
  root_type: "app" | "group" | "user" | "planbook"
  primary_possessive: PRIMARY_POSSESSIVE | null
  primary_verb: PRIMARY_VERB
  primary_object_type: PRIMARY_OBJECT
  primary_object_id: string | null
  primary_object_id_and_ancestor_ids: Array<string> | null
  primary_object_specifier: PRIMARY_OBJECT_SPECIFIER
  adverbial_verb: ADVERBIAL_VERB
  adverbial_object_type: ADVERBIAL_OBJECT
  adverbial_possessive: ADVERBIAL_POSSESSIVE | null
  adverbial_object_specifier: ADVERBIAL_OBJECT_SPECIFIER
  adverbial_object_id: string | null
  adverbial_object_id_and_ancestor_ids: Array<string> | null
}

export function createBlankEvent() {
  return {
    date_plan_scheduled_for: null,
    adverbial_verb: null,
    planbook_id: null,
    primary_possessive: null,
    primary_verb: null,
    primary_object_type: null,
    primary_object_id: null,
    primary_object_id_and_ancestor_ids: null,
    primary_object_specifier: null,
    adverbial_object_type: null,
    adverbial_possessive: null,
    adverbial_object_specifier: null,
    adverbial_object_id: null,
    adverbial_object_id_and_ancestor_ids: null,
  }
}
